import React, {
  useEffect,
  useLayoutEffect, // ADDED
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  X,
  FileText,
  ExternalLink,
  Ticket,
  AlignLeft,
  Paperclip,
  Plus, // ADDED: the "add attachment" box
} from "lucide-react";
import { normalizeAttachments } from "./NeedHelpAttachments";
import NeedHelpTicketChatPreview from "./NeedHelpTicketChatPreview";
import styles from "./NeedHelpTickets.module.css";
import NeedHelpPdfViewer, { needsPdfViewer } from "./NeedHelpPdfViewer";
import {
  addTicketAttachments,
  deleteTicketAttachment,
} from "../../lib/needHelpApi"; // ADDED

// Full screen with the ticket details, then the attachments (one row that
// scrolls sideways, first box = add more), then a small chat box. Clicking an
// attachment opens a popup with the full image / PDF.

const ACTIVE_STATUSES = ["Open", "In Progress", "Reopened"];

// ADDED: attachment rules (same as the Create Ticket form, but 10 files)
const MAX_ATTACHMENTS = 10;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "application/pdf"];

// ADDED
const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch (_e) {
    return "";
  }
};

const statusClass = (status) => {
  switch (status) {
    case "Open":
      return styles.statusOpen;
    case "In Progress":
      return styles.statusProgress;
    case "Reopened":
      return styles.statusReopened;
    case "Resolved":
      return styles.statusResolved;
    case "Closed":
      return styles.statusClosed;
    default:
      return styles.statusClosed;
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// CHANGED: two new props
//   onAttachmentsChange(ticketId, attachments) -> parent updates its ticket list
//   onAttachmentsUploaded() -> parent shows the existing "uploaded" popup
const NeedHelpTicketDetails = ({
  ticket,
  onBack,
  onChatRead,
  scrollToBottom = false,
  onAttachmentsChange, // ADDED
  onAttachmentsUploaded, // ADDED
}) => {
  const files = useMemo(
    () => normalizeAttachments(ticket.attachments),
    [ticket.attachments],
  );
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null); // ADDED

  // ADDED: upload / delete state
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null); // file waiting for confirm
  const [deleting, setDeleting] = useState(false);

  // ADDED: the description is cut after 3 lines; "Read more" opens it in a popup
  const descRef = useRef(null);
  const [descTooLong, setDescTooLong] = useState(false);
  const [descOpen, setDescOpen] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = descRef.current;
      if (!el) return;
      setDescTooLong(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ticket.description]);

  useEffect(() => {
    if (!scrollToBottom) return undefined;
    const timer = setTimeout(() => {
      const el = bodyRef.current;
      if (!el) return;
      const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollToBottom]);

  const isActive = ACTIVE_STATUSES.includes(ticket.status);
  // The attachment shown in the popup (null = popup closed).
  const [preview, setPreview] = useState(null);

  // CHANGED: Esc closes the open popup only (delete confirm or preview).
  // Capture + stop, so the Need Help panel's own Esc handler does not also
  // close the whole panel.
  useEffect(() => {
    if (!preview && !deleteTarget && !descOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      if (deleting) return;
      if (deleteTarget) setDeleteTarget(null);
      else if (descOpen) setDescOpen(false);
      else setPreview(null);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [preview, deleteTarget, descOpen, deleting]);

  // ADDED: user chose files with the "+" box -> check them, then upload at once
  const handleFilesPicked = async (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    if (!picked.length || uploading) return;
    setAttachError("");

    const oversized = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (oversized) {
      setAttachError(`"${oversized.name}" is over the 5MB attachment limit.`);
      return;
    }

    const invalid = picked.find((f) => !ALLOWED_TYPES.includes(f.type));
    if (invalid) {
      setAttachError(
        `"${invalid.name}" is not a supported file. Choose PNG, JPG, or PDF.`,
      );
      return;
    }

    // uploaded + selected must be 10 at most
    const remaining = Math.max(MAX_ATTACHMENTS - files.length, 0);
    if (picked.length > remaining) {
      setAttachError(
        remaining > 0
          ? `Maximum ${MAX_ATTACHMENTS} attachments only. You can add ${remaining} more.`
          : `Maximum ${MAX_ATTACHMENTS} attachments only.`,
      );
      return;
    }

    const token = getToken();
    if (!token) {
      setAttachError("Please sign in to upload attachments.");
      return;
    }

    setUploading(true);
    try {
      const updated = await addTicketAttachments({
        ticketId: ticket.ticketId,
        userId: ticket.userId,
        files: picked,
        token,
      });
      onAttachmentsChange?.(ticket.ticketId, updated);
      onAttachmentsUploaded?.(); // shows the existing "uploaded" popup
    } catch (err) {
      setAttachError(err.message || "Could not upload. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // ADDED: user pressed "Yes, delete" in the confirm popup
  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const token = getToken();
    if (!token) {
      setDeleteTarget(null);
      setAttachError("Please sign in to delete attachments.");
      return;
    }
    setDeleting(true);
    setAttachError("");
    try {
      const updated = await deleteTicketAttachment({
        ticketId: ticket.ticketId,
        userId: ticket.userId,
        url: deleteTarget.url,
        token,
      });
      onAttachmentsChange?.(ticket.ticketId, updated);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteTarget(null);
      setAttachError(err.message || "Could not delete. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // ADDED: small cross button in the corner of every attachment
  const renderDeleteBtn = (file) => (
    <button
      type="button"
      className={styles.attachTileDelete}
      onClick={(e) => {
        e.stopPropagation();
        setAttachError("");
        setDeleteTarget(file);
      }}
      aria-label={`Delete ${file.filename}`}
      title="Delete"
    >
      <X size={11} strokeWidth={3} aria-hidden="true" />
    </button>
  );

  const renderTile = (file, i) => {
    // Image: the whole tile opens the popup.
    if (file.isImage) {
      return (
        <div className={styles.attachTile} key={`${file.url}-${i}`}>
          <button
            type="button"
            className={styles.attachTileBtn}
            onClick={() => setPreview(file)}
            aria-label={`Open ${file.filename}`}
          >
            <img
              src={file.url}
              alt={file.filename}
              className={styles.attachTileImg}
            />
          </button>
          {renderDeleteBtn(file)}
        </div>
      );
    }

    // PDF = small card (icon + "PDF" + file name). Opens the popup.
    if (file.isPdf) {
      return (
        <div className={styles.attachTile} key={`${file.url}-${i}`}>
          <button
            type="button"
            className={styles.attachTileBtn}
            onClick={() => setPreview(file)}
            aria-label={`Open ${file.filename}`}
          >
            <span className={styles.attachTilePdf}>
              <FileText size={26} strokeWidth={1.8} aria-hidden="true" />
              <span className={styles.attachTilePdfTag}>PDF</span>
              <span className={styles.attachTilePdfName}>{file.filename}</span>
            </span>
          </button>
          {renderDeleteBtn(file)}
        </div>
      );
    }

    // Any other file type: a plain tile that opens the popup.
    return (
      <div className={styles.attachTile} key={`${file.url}-${i}`}>
        <button
          type="button"
          className={styles.attachTileBtn}
          onClick={() => setPreview(file)}
        >
          <span className={styles.attachTileOther}>
            <FileText size={22} strokeWidth={1.8} aria-hidden="true" />
            <span>{file.filename}</span>
          </span>
        </button>
        {renderDeleteBtn(file)}
      </div>
    );
  };

  const renderPreview = (file) => {
    if (file.isImage) {
      return (
        <img src={file.url} alt={file.filename} className={styles.previewImg} />
      );
    }
    if (file.isPdf) {
      if (needsPdfViewer()) {
        return <NeedHelpPdfViewer url={file.url} title={file.filename} />;
      }
      return (
        <iframe
          src={`${file.url}#toolbar=0&navpanes=0`}
          title={file.filename}
          className={styles.previewFrame}
        />
      );
    }
    return (
      <p className={styles.previewNote}>
        This file type can't be previewed here.
      </p>
    );
  };

  return (
    <div className={styles.screen} role="dialog" aria-label="Ticket details">
      <header className={styles.headBar}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={onBack}
          aria-label="Back"
        >
          <svg
            width="9"
            height="15"
            viewBox="21 18.34 7.66 13.32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M21.293 25.7071C20.9025 25.3166 20.9025 24.6834 21.293 24.2929L26.9499 18.636C27.3404 18.2455 27.9736 18.2455 28.3641 18.636C28.7546 19.0266 28.7546 19.6597 28.3641 20.0503L23.4144 25L28.3641 29.9497C28.7546 30.3403 28.7546 30.9734 28.3641 31.364C27.9736 31.7545 27.3404 31.7545 26.9499 31.364L21.293 25.7071Z"
              fill="#0A0A0A"
              fillOpacity="0.9"
            />
          </svg>
        </button>
        <div className={styles.headText}>
          <h2 className={styles.headTitle}>Ticket Details</h2>
          <p className={styles.headSub}>View and continue the conversation</p>
        </div>
      </header>

      <div ref={bodyRef} className={styles.body}>
        <div className={styles.detailsCard}>
          <div className={styles.detailsTop}>
            <span className={styles.detailsIdIcon} aria-hidden="true">
              <Ticket size={16} strokeWidth={2} />
            </span>
            <span className={styles.idValue}>{ticket.ticketId}</span>
            <span
              className={`${styles.status} ${
                isActive ? styles.statusActive : statusClass(ticket.status)
              }`}
            >
              {ticket.status}
            </span>
            <span className={styles.detailsDate}>
              {formatDate(ticket.createdAt)}
            </span>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailIcon} aria-hidden="true">
              <FileText size={18} strokeWidth={1.9} />
            </span>
            <div className={styles.cell}>
              <span className={styles.label}>Subject</span>
              <span className={styles.value}>{ticket.subject || "-"}</span>
            </div>
          </div>

          <div className={styles.detailRow}>
            <span className={styles.detailIcon} aria-hidden="true">
              <AlignLeft size={18} strokeWidth={1.9} />
            </span>
            <div className={styles.cell}>
              <span className={styles.label}>Description</span>
              {/* CHANGED: max 3 lines; if it is longer, "... Read more" sits
                  at the end of the 3rd line and opens the full text */}
              <div className={styles.descWrap}>
                <p ref={descRef} className={`${styles.descFull} ${styles.descClamp}`}>
                  {ticket.description || "-"}
                </p>
                {descTooLong && (
                  <button
                    type="button"
                    className={styles.descMore}
                    onClick={() => setDescOpen(true)}
                  >
                    <span className={styles.descDots}>… </span>Read more
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CHANGED: badge shows used / maximum, e.g. "6/10" */}
        <div className={styles.attachHead}>
          <span className={styles.attachHeadIcon} aria-hidden="true">
            <Paperclip size={15} strokeWidth={2.1} />
          </span>
          <h3 className={styles.attachHeadTitle}>Attachments</h3>
          <span className={styles.attachHeadBadge}>
            {files.length}/{MAX_ATTACHMENTS}
          </span>
        </div>

        {/* CHANGED: always shown; first box = add more, then the files in one
            row that scrolls sideways */}
        <div className={styles.attachGrid}>
          <div className={styles.attachTile}>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenFileInput}
              accept="image/png,image/jpeg,application/pdf"
              multiple
              onChange={handleFilesPicked}
              aria-label="Choose attachments to upload"
            />
            <button
              type="button"
              className={`${styles.attachTileBtn} ${styles.attachAddBtn}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || deleting}
              aria-label="Add attachments"
              title="Add attachments"
            >
              {uploading ? (
                <span className={styles.attachAddSpinner} aria-label="Uploading" />
              ) : (
                <Plus size={22} strokeWidth={2.2} aria-hidden="true" />
              )}
            </button>
          </div>
          {files.map(renderTile)}
        </div>

        {/* ADDED: error under the attachments */}
        {attachError && (
          <p className={styles.attachError} role="alert">
            {attachError}
          </p>
        )}

        <NeedHelpTicketChatPreview ticket={ticket} onChatRead={onChatRead} />
      </div>

      {preview &&
        createPortal(
          <div
            className={styles.attachOverlay}
            onClick={() => setPreview(null)}
          >
            <div
              className={styles.popupStack}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.attachPopup}
                role="dialog"
                aria-modal="true"
                aria-label={preview.filename}
              >
                <div className={styles.attachPopupHead}>
                  <h4 className={styles.attachPopupTitle}>{preview.filename}</h4>
                </div>
                <div className={styles.attachPopupBody}>
                  <div className={styles.previewBox}>{renderPreview(preview)}</div>
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.openLink}
                  >
                    <ExternalLink size={14} strokeWidth={2} aria-hidden="true" />
                    Open in new tab
                  </a>
                </div>
              </div>
              <button
                type="button"
                className={styles.popupCloseBelow}
                onClick={() => setPreview(null)}
                aria-label="Close"
              >
                <X size={22} strokeWidth={2.2} />
              </button>
            </div>
          </div>,
          document.body,
        )}

      {/* ADDED: full description popup, close button below it */}
      {descOpen &&
        createPortal(
          <div
            className={styles.attachOverlay}
            onClick={() => setDescOpen(false)}
          >
            <div
              className={styles.popupStack}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={styles.attachPopup}
                role="dialog"
                aria-modal="true"
                aria-label="Description"
              >
                <div className={`${styles.attachPopupHead} ${styles.descPopupHead}`}>
                  <h4 className={styles.attachPopupTitle}>Description</h4>
                </div>
                <div className={`${styles.attachPopupBody} ${styles.descPopupBody}`}>
                  <p className={styles.descFull}>{ticket.description}</p>
                </div>
              </div>
              <button
                type="button"
                className={styles.popupCloseBelow}
                onClick={() => setDescOpen(false)}
                aria-label="Close"
              >
                <X size={22} strokeWidth={2.2} />
              </button>
            </div>
          </div>,
          document.body,
        )}

      {/* ADDED: "delete this attachment?" confirm popup */}
      {deleteTarget &&
        createPortal(
          <div
            className={styles.attachOverlay}
            onClick={() => !deleting && setDeleteTarget(null)}
          >
            <div
              className={styles.confirmCard}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="delete-attachment-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h4 id="delete-attachment-title" className={styles.confirmTitle}>
                Delete attachment?
              </h4>
              <p className={styles.confirmText}>
                "{deleteTarget.filename}" will be removed from this ticket. This
                can't be undone.
              </p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.confirmCancel}
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.confirmDelete}
                  onClick={confirmDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Yes, delete"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default NeedHelpTicketDetails;
